import {useState} from 'react'
import {z} from 'zod'
import {useAuth} from 'features/common/context/auth'
import {phoneRegex} from 'features/common/core/regex'

const signupSchema = z.object({
    name: z
        .string()
        .min(4, {message: 'VALIDATION_TOO_SHORT'})
        .max(40, {message: 'VALIDATION_TOO_LONG'}),
    organization: z
        .string()
        .min(4, {message: 'VALIDATION_TOO_SHORT'})
        .max(40, {message: 'VALIDATION_TOO_LONG'}),
    country: z.string(),
    phone: z
        .string()
        .min(4, {message: 'VALIDATION_TOO_SHORT'})
        .max(40, {message: 'VALIDATION_TOO_LONG'})
        .regex(phoneRegex, 'VALIDATION_PHONE_FORMAT'),
    email: z
        .string()
        .email({message: 'VALIDATION_INVALID_EMAIL'})
        .min(4, {message: 'VALIDATION_TOO_SHORT'})
        .max(40, {message: 'VALIDATION_TOO_LONG'}),
    password: z
        .string()
        .min(4, {message: 'VALIDATION_TOO_SHORT'})
        .max(40, {message: 'VALIDATION_TOO_LONG'})
        .refine((password) => !password.includes(' '), 'VALIDATION_NO_SPACE'),
    tos: z.boolean(),
})

type SignupData = z.infer<typeof signupSchema>
type SignupErrors = Partial<Record<keyof SignupData, string>>

type OnChangeHandler = (args: {
    prop: keyof SignupData
    value: SignupData[keyof SignupData]
}) => void

const emptyData: SignupData = {
    name: '',
    organization: '',
    country: '',
    phone: '',
    email: '',
    password: '',
    tos: true,
} as const

const useSignup = () => {
    const {register, registerError, registerLoading} = useAuth()
    const [data, setData] = useState<SignupData>(emptyData)
    const [errors, setErrors] = useState<SignupErrors>({})

    const onChange: OnChangeHandler = ({prop, value}) => {
        setData((sd) => {
            const newData = {...sd, [prop]: value}
            const parseRes = signupSchema.safeParse(newData)

            if (!parseRes.success) {
                const formattedErrors = parseRes.error.flatten()
                const currentError = formattedErrors.fieldErrors[prop]
                setErrors((errs) => {
                    if (currentError) {
                        return {
                            ...errs,
                            [prop]: currentError[0],
                        }
                    }
                    const newErrs = {...errs}
                    delete newErrs[prop]
                    return newErrs
                })
            } else {
                setErrors({})
            }

            return newData
        })
    }

    const onSubmit = () => {
        register && register(data)
    }

    return {
        data,
        errors,
        onChange,
        onSubmit,
        onSubmitError: registerError,
        loading: registerLoading,
    }
}

export default useSignup
